import LogRocket from 'logrocket';

let isInitialized = false;

// ── LogRocket recording budget ──────────────────────────
// Adjust these two values as traffic changes.
// MONTHLY_EXPECTED_SESSIONS = estimated paid-subscriber sessions per month
// (the isPaidUser gate inside initLogRocket ensures only paid sessions are recorded).
const MONTHLY_EXPECTED_SESSIONS = 300_000;
const TARGET_MONTHLY_RECORDINGS = 1_000;
// ────────────────────────────────────────────────────────

const SAMPLE_RATE = TARGET_MONTHLY_RECORDINGS / MONTHLY_EXPECTED_SESSIONS;

// Maximum age for a recording decision before re-sampling
// Prevents multi-day recordings from users who leave tabs open
const SESSION_MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

const STORAGE_KEY = 'logrocket_sample';

/**
 * Get the recording decision, persisted for the browser session with expiration.
 * Uses sessionStorage so reloads/navigation maintain recording state,
 * but new tabs get a fresh random decision.
 * After SESSION_MAX_AGE_MS, the decision expires and a new random choice is made.
 */
function getShouldRecord() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const { decision, timestamp } = JSON.parse(stored);
      const age = Date.now() - timestamp;
      if (age < SESSION_MAX_AGE_MS) {
        return decision;
      }
      // Expired - fall through to make new decision
    }
  } catch {
    // Invalid stored data - fall through to make new decision
  }

  const decision = Math.random() < SAMPLE_RATE;
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      decision,
      timestamp: Date.now(),
    })
  );
  return decision;
}

const shouldRecord = getShouldRecord();

/**
 * Initialize LogRocket for session recording.
 * Only initializes once, safe to call multiple times.
 * Respects SAMPLE_RATE to control session volume and only records paid users.
 */
export const initLogRocket = ({ isPaidUser = false } = {}) => {
  if (isInitialized || !shouldRecord || !isPaidUser) return;

  LogRocket.init('igjraa/workshop', {
    network: {
      requestSanitizer: (request) =>
        // Drop the request/response pair: OAuth codes, state and temporary edit
        // links must not be captured by session replay's network recorder.
        request.url.includes('/integrations/canva/') ? null : request,
    },
  });
  isInitialized = true;
};

/**
 * Identify the current user for LogRocket sessions.
 * Call after user is authenticated.
 */
export const identifyLogRocketUser = (userId, traits = {}) => {
  if (!shouldRecord || !isInitialized) return;
  LogRocket.identify(userId, traits);
};

/**
 * Track a custom event in LogRocket.
 * Optional - LogRocket auto-captures most interactions.
 */
export const trackLogRocketEvent = (eventName, properties = {}) => {
  if (!shouldRecord || !isInitialized) return;
  LogRocket.track(eventName, properties);
};

export default LogRocket;
